import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PrivateRoute } from "@/components/PrivateRoute";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import Enroll from "@/pages/Enroll";

import TeacherDashboard from "@/pages/teacher/TeacherDashboard";
import StudentDetail from "@/pages/teacher/StudentDetail";
import TeacherModules from "@/pages/teacher/LearningModules";
import TeacherModuleDetail from "@/pages/teacher/TeacherModuleDetail";
import TeacherSettings from "@/pages/teacher/Settings";
import EditProfile from "@/pages/teacher/EditProfile";
import ChangeEmail from "@/pages/teacher/ChangeEmail";
import ChangeMobile from "@/pages/teacher/ChangeMobile";
import ChangePassword from "@/pages/teacher/ChangePassword";

import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentModules from "@/pages/student/StudentModules";
import StudentModuleDetail from "@/pages/student/StudentModuleDetail";
import QuizLanding from "@/pages/student/QuizLanding";
import QuizScreen from "@/pages/student/QuizScreen";
import StudentSettings from "@/pages/student/StudentSettings";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSchools from "@/pages/admin/Schools";
import AdminTeachers from "@/pages/admin/Teachers";
import AdminStudents from "@/pages/admin/Students";
import AdminModules from "@/pages/admin/Modules";
import ModuleSubmodules from "@/pages/admin/ModuleSubmodules";
import BulkImport from "@/pages/admin/BulkImport";
import BackupRestore from "@/pages/admin/BackupRestore";
import VRSessions from "@/pages/admin/VRSessions";
import VRQuizResults from "@/pages/admin/VRQuizResults";
import DeveloperDocs from "@/pages/admin/DeveloperDocs";

import ParentDashboard from "@/pages/parent/ParentDashboard";

const RootRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const home = {
    teacher: "/teacher/dashboard", student: "/student/dashboard",
    admin: "/admin/dashboard",     parent:  "/parent/dashboard",
  }[user.role] || "/login";
  return <Navigate to={home} replace />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

const AppRoutes = () => {
  // Process Google OAuth session_id synchronously, before any route/auth logic runs
  if (window.location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/enroll" element={<PrivateRoute role="student"><Enroll /></PrivateRoute>} />

            {/* Teacher */}
            <Route path="/teacher/dashboard"        element={<PrivateRoute role="teacher"><TeacherDashboard /></PrivateRoute>} />
            <Route path="/teacher/students/:id"     element={<PrivateRoute role="teacher"><StudentDetail /></PrivateRoute>} />
            <Route path="/teacher/modules"          element={<PrivateRoute role="teacher"><TeacherModules /></PrivateRoute>} />
            <Route path="/teacher/modules/:moduleId" element={<PrivateRoute role="teacher"><TeacherModuleDetail /></PrivateRoute>} />
            <Route path="/teacher/manage/modules"    element={<PrivateRoute role="teacher"><AdminModules role="teacher" /></PrivateRoute>} />
            <Route path="/teacher/manage/modules/:moduleId/submodules" element={<PrivateRoute role="teacher"><ModuleSubmodules role="teacher" /></PrivateRoute>} />
            <Route path="/teacher/settings"         element={<PrivateRoute role="teacher"><TeacherSettings /></PrivateRoute>} />
            <Route path="/teacher/settings/edit-profile"   element={<PrivateRoute role="teacher"><EditProfile /></PrivateRoute>} />
            <Route path="/teacher/settings/change-email"   element={<PrivateRoute role="teacher"><ChangeEmail /></PrivateRoute>} />
            <Route path="/teacher/settings/change-mobile"  element={<PrivateRoute role="teacher"><ChangeMobile /></PrivateRoute>} />
            <Route path="/teacher/settings/change-password"element={<PrivateRoute role="teacher"><ChangePassword /></PrivateRoute>} />

            {/* Student */}
            <Route path="/student/dashboard"     element={<PrivateRoute role="student"><StudentDashboard /></PrivateRoute>} />
            <Route path="/student/modules"       element={<PrivateRoute role="student"><StudentModules /></PrivateRoute>} />
            <Route path="/student/modules/:moduleId" element={<PrivateRoute role="student"><StudentModuleDetail /></PrivateRoute>} />
            <Route path="/student/quiz"          element={<PrivateRoute role="student"><QuizLanding /></PrivateRoute>} />
            <Route path="/student/quiz/:moduleId"element={<PrivateRoute role="student"><QuizScreen /></PrivateRoute>} />
            <Route path="/student/settings"      element={<PrivateRoute role="student"><StudentSettings /></PrivateRoute>} />

            {/* Admin */}
            <Route path="/admin/dashboard" element={<PrivateRoute role="admin"><AdminDashboard /></PrivateRoute>} />
            <Route path="/admin/schools"   element={<PrivateRoute role="admin"><AdminSchools /></PrivateRoute>} />
            <Route path="/admin/teachers"  element={<PrivateRoute role="admin"><AdminTeachers /></PrivateRoute>} />
            <Route path="/admin/students"  element={<PrivateRoute role="admin"><AdminStudents /></PrivateRoute>} />
            <Route path="/admin/modules"   element={<PrivateRoute role="admin"><AdminModules /></PrivateRoute>} />
            <Route path="/admin/modules/:moduleId/submodules" element={<PrivateRoute role="admin"><ModuleSubmodules /></PrivateRoute>} />
            <Route path="/admin/import"    element={<PrivateRoute role="admin"><BulkImport /></PrivateRoute>} />
            <Route path="/admin/backup"    element={<PrivateRoute role="admin"><BackupRestore /></PrivateRoute>} />
            <Route path="/admin/vr-sessions"     element={<PrivateRoute role="admin"><VRSessions /></PrivateRoute>} />
            <Route path="/admin/vr-quiz"         element={<PrivateRoute role="admin"><VRQuizResults /></PrivateRoute>} />
            <Route path="/admin/developer-docs"  element={<PrivateRoute role="admin"><DeveloperDocs /></PrivateRoute>} />

            {/* Parent */}
            <Route path="/parent/dashboard" element={<PrivateRoute role="parent"><ParentDashboard /></PrivateRoute>} />

            <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
};

export default App;
